import { type FormEvent, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import type { InputErrorType } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  ACTIVITIES_AVAILABILITY_PATH,
  ACTIVITY_URL_PARAM_KEYS,
} from "@overrides/utilities";
import type { ActivityLocationSuggestion } from "@tenant/activities";
import dayjs from "dayjs";
import {
  ActivitiesAvailabilityActions,
  ActivitiesAvailabilitySelectors,
} from "../store";

type FormErrors = {
  place?: InputErrorType;
  dateRange?: InputErrorType;
};

export const useActivitySearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { t } = useI18nContext();

  const dateRange = useSelector(ActivitiesAvailabilitySelectors.getDateRange);
  const travelers = useSelector(ActivitiesAvailabilitySelectors.getTravelers);
  const place = useSelector(ActivitiesAvailabilitySelectors.getPlace);

  const [formError, setFormError] = useState<FormErrors>({});

  const handleDateRangeFromChange = useCallback(
    (date: string | undefined) => {
      dispatch(ActivitiesAvailabilityActions.setNewDateFrom(date));
      setFormError((prev) => ({ ...prev, dateRange: undefined }));
    },
    [dispatch]
  );

  const handleDateRangeUntilChange = useCallback(
    (date: string | undefined) => {
      dispatch(ActivitiesAvailabilityActions.setNewDateUntil(date));
      setFormError((prev) => ({ ...prev, dateRange: undefined }));
    },
    [dispatch]
  );

  const handlePlaceChange = useCallback(
    (place: ActivityLocationSuggestion | null) => {
      dispatch(ActivitiesAvailabilityActions.setNewPlace(place));
      setFormError((prev) => ({ ...prev, place: undefined }));
    },
    [dispatch]
  );

  const handlePlaceFocus = useCallback(() => {
    setFormError((prev) => ({ ...prev, place: undefined }));
  }, []);

  const handleTravelersChange = (count: number) => {
    dispatch(ActivitiesAvailabilityActions.setNewTravelers(count));
  };

  const isFormValid = useMemo(() => {
    return (
      dateRange?.from != null &&
      place != null &&
      place.label != null &&
      place.label.trim() !== ""
    );
  }, [dateRange, place]);

  const availabilityPath = useMemo(() => {
    if (isFormValid) {
      const destinationCode = encodeURIComponent(
        place?.label +
          (place?.subLabel != null && place?.subLabel.trim() !== ""
            ? `, ${place?.subLabel}`
            : "")
      );

      const untilDate = dateRange?.until ?? dateRange.from;

      const searchParams = new URLSearchParams({
        [ACTIVITY_URL_PARAM_KEYS.FROM_DATE]: dayjs(dateRange.from).format(
          "YYYY-MM-DD"
        ),
        [ACTIVITY_URL_PARAM_KEYS.UNTIL_DATE]:
          dayjs(untilDate).format("YYYY-MM-DD"),
        ...(travelers != null && {
          [ACTIVITY_URL_PARAM_KEYS.ADULTS_COUNT]: travelers.toString(),
        }),
      });

      return `${ACTIVITIES_AVAILABILITY_PATH}/${destinationCode}?${searchParams.toString()}`;
    } else {
      return null;
    }
  }, [dateRange, place, travelers, isFormValid]);

  const handleSearch = useCallback(() => {
    if (availabilityPath != null) {
      navigate(availabilityPath);
    }
  }, [availabilityPath, navigate]);

  const onFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!isFormValid) {
        const errors: FormErrors = {};
        if (!place) {
          errors.place = { message: undefined };
        }
        if (!dateRange?.from) {
          errors.dateRange = { message: undefined };
        }
        setFormError(errors);
        return;
      }

      handleSearch();
    },
    [isFormValid, place, dateRange, handleSearch]
  );

  return {
    context: {
      dateRange,
      travelers,
      place,
      locationLabel: t("core-activities.search.locationLabel"),
      searchButtonLabel: t("core-activities.search.searchButtonLabel"),
      isFormValid,
      availabilityPath,
      formError,
    },
    handlers: {
      handleDateRangeFromChange,
      handleDateRangeUntilChange,
      handlePlaceChange,
      handlePlaceFocus,
      handleTravelersChange,
      onFormSubmit,
      handleSearch,
    },
  };
};
